import React, { useState, useEffect, useCallback } from 'react';
import { getPendingHealthInsuranceRenewals, approveHealthInsuranceRenewal, getImageUrl } from '../../model/api';
import LoadingSpinner from './common/LoadingSpinner';
import { toast } from 'react-toastify';

const RenewalRequestsTab = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [approvingId, setApprovingId] = useState(null);
    const [showApprovalModal, setShowApprovalModal] = useState(false);

    // Approval Form State
    const [approvalData, setApprovalData] = useState({
        policy_term_years: 1,
        review_commission_percentage: '1', // Default 1%
        premium_by_year: [{ year: 1, premium_amount: '', commission_percentage: '' }],
        document_pdf: null,
        insurance_card_pdf: null
    });

    const [selectedPolicy, setSelectedPolicy] = useState(null);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const result = await getPendingHealthInsuranceRenewals();
            if (result.ok) {
                setRequests(result.data || []);
            } else {
                setRequests([]);
                toast.error('Failed to fetch renewal requests');
            }
        } catch (error) {
            console.error('Error fetching renewal requests:', error);
            toast.error('Error loading requests');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleApproveClick = (policy) => {
        setSelectedPolicy(policy);
        setApprovalData({
            policy_term_years: 1,
            review_commission_percentage: '1',
            premium_by_year: [{ year: 1, premium_amount: '', commission_percentage: policy.commission_percentage || '' }],
            document_pdf: null,
            insurance_card_pdf: null
        });
        setShowApprovalModal(true);
    };

    const handleTermChange = (e) => {
        const term = parseInt(e.target.value);
        const premiums = [];
        for (let i = 1; i <= term; i++) {
            // Preserve existing values if expanding
            const existing = approvalData.premium_by_year.find(p => p.year === i);
            premiums.push(existing || { year: i, premium_amount: '', commission_percentage: '' });
        }
        setApprovalData(prev => ({
            ...prev,
            policy_term_years: term,
            premium_by_year: premiums
        }));
    };

    const handleYearlyDataChange = (year, field, value) => {
        setApprovalData(prev => ({
            ...prev,
            premium_by_year: prev.premium_by_year.map(p =>
                p.year === year ? { ...p, [field]: value } : p
            )
        }));
    };

    const handleFileChange = (e, field) => {
        if (e.target.files[0]) {
            setApprovalData(prev => ({ ...prev, [field]: e.target.files[0] }));
        }
    };

    const handleApproveSubmit = async (e) => {
        e.preventDefault();
        if (!selectedPolicy) return;

        // Validation
        const totalPremium = approvalData.premium_by_year.reduce((sum, p) => sum + Number(p.premium_amount || 0), 0);
        if (totalPremium <= 0) {
            toast.error('Please enter valid premium amounts');
            return;
        }

        setApprovingId(selectedPolicy._id);
        try {
            const formData = new FormData();
            formData.append('policy_term_years', approvalData.policy_term_years);
            // formData.append('commission_percentage', approvalData.commission_percentage); // Removed single comm percentage
            formData.append('review_commission_percentage', approvalData.review_commission_percentage);

            // Format premiums for backend
            const premiumsToSend = approvalData.premium_by_year.map(p => ({
                year: p.year,
                premium_amount: Number(p.premium_amount),
                commission_percentage: Number(p.commission_percentage)
            }));
            formData.append('premium_by_year', JSON.stringify(premiumsToSend));

            if (approvalData.document_pdf) {
                formData.append('document_pdf', approvalData.document_pdf);
            }
            if (approvalData.insurance_card_pdf) {
                formData.append('insurance_card_pdf', approvalData.insurance_card_pdf);
            }

            const result = await approveHealthInsuranceRenewal(selectedPolicy._id, formData);
            if (result.ok) {
                toast.success('Renewal Approved! Payout processing started.');
                setShowApprovalModal(false);
                fetchRequests();
            } else {
                toast.error(result.error || 'Failed to approve renewal');
            }
        } catch (error) {
            console.error('Error approving renewal:', error);
            toast.error('Error approving renewal');
        } finally {
            setApprovingId(null);
        }
    };

    // Calculation Preview
    const totalPremium = approvalData.premium_by_year.reduce((sum, p) => sum + Number(p.premium_amount || 0), 0);

    // New Calculation Logic: Sum of (Premium * Commission%) per year
    const baseCommission = approvalData.premium_by_year.reduce((sum, p) => {
        const premium = Number(p.premium_amount || 0);
        const commPct = Number(p.commission_percentage || 0);
        return sum + (premium * (commPct / 100));
    }, 0);

    const reviewBonus = totalPremium * (parseFloat(approvalData.review_commission_percentage || 0) / 100);
    const totalCommission = baseCommission + reviewBonus;

    if (loading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">Policies for Review</h3>
                    <button onClick={fetchRequests} className="text-indigo-600 hover:text-indigo-800">
                        Refresh
                    </button>
                </div>

                {requests.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No pending renewal requests found.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Policy No</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Existing Expiry</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested By</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {requests.map((req) => (
                                    <tr key={req._id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{req.policy_number}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div>{req.customer_name}</div>
                                            <div className="text-xs">{req.customer_phone}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(req.policy_expires_date).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {req.partner_id?.name || 'Unknown'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                Pending Review
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => handleApproveClick(req)}
                                                className="text-white bg-green-600 hover:bg-green-700 px-3 py-1 rounded-md text-sm"
                                            >
                                                Approve / Renew
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Approval Modal */}
            {showApprovalModal && selectedPolicy && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8">
                        <div className="flex justify-between items-center p-5 border-b border-gray-200">
                            <h3 className="text-xl font-medium text-gray-900">
                                Renew Policy: {selectedPolicy.policy_number}
                            </h3>
                            <button
                                onClick={() => setShowApprovalModal(false)}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <span className="text-2xl">&times;</span>
                            </button>
                        </div>

                        <form onSubmit={handleApproveSubmit} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                                    <input type="text" value={selectedPolicy.customer_name} disabled className="w-full bg-gray-100 border rounded px-3 py-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Policy Term (Years)</label>
                                    <select
                                        name="policy_term_years"
                                        value={approvalData.policy_term_years}
                                        onChange={handleTermChange}
                                        className="w-full border rounded px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    >
                                        {[1, 2, 3, 4, 5].map(y => <option key={y} value={y}>{y} Year{y > 1 ? 's' : ''}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Premium & Commission Breakup (per year)</label>
                                <div className="space-y-2 bg-gray-50 p-3 rounded-md">
                                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 mb-1">
                                        <div className="col-span-2">Year</div>
                                        <div className="col-span-5">Premium (₹)</div>
                                        <div className="col-span-5">Commission (%)</div>
                                    </div>

                                    {approvalData.premium_by_year.map((p) => (
                                        <div key={p.year} className="grid grid-cols-12 gap-2 items-center">
                                            <span className="col-span-2 text-sm font-medium text-gray-600">Year {p.year}</span>
                                            <div className="col-span-5">
                                                <input
                                                    type="number"
                                                    value={p.premium_amount}
                                                    onChange={(e) => handleYearlyDataChange(p.year, 'premium_amount', e.target.value)}
                                                    className="w-full border rounded px-2 py-1 text-sm"
                                                    placeholder="Premium"
                                                    required
                                                />
                                            </div>
                                            <div className="col-span-5">
                                                <input
                                                    type="number"
                                                    value={p.commission_percentage}
                                                    onChange={(e) => handleYearlyDataChange(p.year, 'commission_percentage', e.target.value)}
                                                    className="w-full border rounded px-2 py-1 text-sm"
                                                    placeholder="Comm %"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <div className="flex justify-between items-center pt-2 border-t mt-2">
                                        <span className="font-semibold text-gray-700">Total Premium:</span>
                                        <span className="font-bold text-gray-900">₹{totalPremium.toLocaleString('en-IN')}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-green-700 mb-1">Review Commission (%)</label>
                                    <input
                                        type="number"
                                        name="review_commission_percentage"
                                        value={approvalData.review_commission_percentage}
                                        onChange={(e) => setApprovalData({ ...approvalData, review_commission_percentage: e.target.value })}
                                        className="w-full border border-green-300 rounded px-3 py-2 bg-green-50"
                                        placeholder="1"
                                    />
                                    <p className="text-xs text-green-600 mt-1">Extra bonus on total premium</p>
                                </div>
                            </div>

                            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                                <h4 className="text-sm font-semibold text-blue-800 mb-2">Commission Preview</h4>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">Base Commission:</span>
                                    <span>₹{baseCommission.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-green-600 font-medium">Review Bonus ({approvalData.review_commission_percentage}%):</span>
                                    <span className="text-green-600 font-medium">+₹{reviewBonus.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between font-bold border-t border-blue-200 pt-2 mt-1">
                                    <span className="text-blue-900">Total Payout:</span>
                                    <span className="text-blue-900">₹{totalCommission.toLocaleString('en-IN')}</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">New Policy Document (PDF)</label>
                                    <input type="file" onChange={(e) => handleFileChange(e, 'document_pdf')} accept=".pdf" className="w-full text-sm" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">New Insurance Card (PDF)</label>
                                    <input type="file" onChange={(e) => handleFileChange(e, 'insurance_card_pdf')} accept=".pdf" className="w-full text-sm" />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowApprovalModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                                // disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={approvingId === selectedPolicy._id}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 font-medium"
                                >
                                    {approvingId === selectedPolicy._id ? 'Processing...' : 'Approve Renewal'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RenewalRequestsTab;
